import { CheckSquareOutlined, CloseSquareOutlined } from '@ant-design/icons';
import { Descriptions, DescriptionsProps } from 'antd';
import { useIntl } from 'react-intl';
import { BtlApplication } from '../../../api/btlApplicationForm/types';
import { TypedFormatMessageFn } from '../../../types/intl';
import { monthsToYears } from '../../../utils/numbers';
import { formatCurrency, formatRegion } from './caseSummaryFormatters';

export type FinancingAndPropertySummaryVariant = 'btl' | 'oo';

interface FinancingAndPropertySummaryProps {
  data?: BtlApplication;
  variant?: FinancingAndPropertySummaryVariant;
}

function formatFtv(ftv?: number | null): string {
  if (ftv === undefined || ftv === null) {
    return '-';
  }

  return `${(ftv * 100).toFixed(0)}%`;
}

export function FinancingAndPropertySummary({ data, variant = 'btl' }: FinancingAndPropertySummaryProps) {
  const { formatMessage }: { formatMessage: TypedFormatMessageFn } = useIntl();

  const depositAmount =
    data?.purpose === 'PURCHASE'
      ? [
          {
            label: formatMessage({ id: 'btlApplicationForm.FINANCING.deposit.label' }),
            children: formatCurrency(data?.depositAmount),
          },
        ]
      : [];

  const variantSpecificItems: DescriptionsProps['items'] =
    variant === 'oo'
      ? [
          {
            label: formatMessage({ id: 'btlApplicationForm.CASE_SUMMARY.affordability.ftv.label' }),
            children: formatFtv(data?.netFinancingFtv),
          },
        ]
      : [
          {
            label: formatMessage({ id: 'btlApplicationForm.PROPERTY.grossMonthlyRentalPayment.label' }),
            children: formatCurrency(data?.property.grossMonthlyRentalPayment),
          },
        ];

  const regionItem: DescriptionsProps['items'] =
    variant === 'oo'
      ? [
          {
            label: formatMessage({ id: 'applicationForm.REGION.label' }),
            children: formatRegion(data?.applicationProperty?.property?.region, formatMessage),
          },
        ]
      : [];

  const items: DescriptionsProps['items'] = [
    {
      label: formatMessage({ id: 'btlApplicationForm.FINANCING.purpose.label' }),
      children: <span className={'capitalize'}>{data?.purpose.toLowerCase()}</span>,
    },
    {
      label: formatMessage({ id: 'btlApplicationForm.FINANCING.paymentProfile.label' }),
      children: <span className={'capitalize'}>{data?.paymentProfile?.toLowerCase().split('_').join(' ')}</span>,
    },
    ...depositAmount,
    {
      label: formatMessage({ id: 'btlApplicationForm.FINANCING.financeAmount.label' }),
      children: formatCurrency(data?.financingAmount),
    },
    {
      label: formatMessage({
        id:
          data?.purpose === 'PURCHASE'
            ? 'btlApplicationForm.PROPERTY.purchasePrice.label'
            : 'btlApplicationForm.PROPERTY.estimatedMarketValue.label',
      }),
      children: formatCurrency(data?.property.purchasePrice),
    },
    ...variantSpecificItems,
    {
      label: formatMessage({ id: 'btlApplicationForm.FINANCING.term.label' }),
      children: monthsToYears(data?.termInMonths ?? 0),
    },
    ...regionItem,
    {
      label: formatMessage({ id: 'btlApplicationForm.FINANCING.term.feesAddedToBalance.label' }),
      children: data?.feesAddedToBalance ? (
        <CheckSquareOutlined style={{ fontSize: 20 }} />
      ) : (
        <CloseSquareOutlined style={{ fontSize: 20 }} />
      ),
    },
  ];

  return (
    <Descriptions
      items={items}
      bordered={true}
      styles={{ label: { textAlign: 'end', alignContent: 'start' } }}
      size={'middle'}
    />
  );
}
